import React, { useCallback, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { buildSemanticQuery } from "@/lib/widgetCatalog";
import { Table2, Download, Loader2, AlertTriangle, Sigma, Rows3, Columns2 } from "lucide-react";
import { cn } from "@/lib/utils";

function toCSV(columns, rows) {
  const esc = (v) => {
    const s = v == null ? "" : String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  return [columns.map(esc).join(","), ...rows.map((r) => columns.map((c) => esc(r[c])).join(","))].join("\n");
}

const cleanFilters = (filters) => filters
  .map(({ __src, dataset_id, key, ...r }) => r)
  .filter((f) => f.field && f.value != null && f.value !== "" && !(Array.isArray(f.value) && f.value.length === 0));

// Physical source columns actually used by the visual (dims, measures, tooltips).
// Governed keys are resolved to fields via the dataset's semantic model.
function usedColumns(config, model) {
  const refs = [...(config?.dimensions || []), ...(config?.measures || []), ...(config?.tooltip_measures || [])];
  const keyMap = {};
  (model?.dimensions || []).forEach((d) => { if (d.key && d.field) keyMap[d.key] = d.field; });
  (model?.measures || []).forEach((m) => { if (m.key && m.field) keyMap[m.key] = m.field; });
  const cols = [];
  refs.forEach((r) => {
    const f = r.field || (r.key ? keyMap[r.key] : null);
    if (f && !cols.includes(f)) cols.push(f);
  });
  return cols;
}

function ResultTable({ columns, rows, testid }) {
  return (
    <div className="rounded-xl border border-[color:var(--glass-border)] overflow-auto max-h-[48vh]" data-testid={testid}>
      <table className="w-full text-xs">
        <thead className="sticky top-0 bg-[color:var(--surface-2)] z-10">
          <tr>{columns.map((c) => <th key={c} className="text-left px-3 py-2 font-medium text-[color:var(--ink-2)] whitespace-nowrap">{c}</th>)}</tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className="border-t border-[color:var(--glass-border)] hover:bg-[color:var(--surface-2)]/60">
              {columns.map((c) => <td key={c} className="px-3 py-1.5 text-[color:var(--ink-2)] whitespace-nowrap">{typeof r[c] === "number" ? Number(r[c]).toLocaleString() : String(r[c] ?? "—")}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// Band drill (items 28-30): resolve a clicked band/bin label back to row filters.
const bandAlias = (d) => d?.alias || (d?.band ? `${d.band.measure}_band` : d?.bin ? `${d.field}_bin` : "");
function rangeFromLabel(label, bin) {
  const breaks = (bin?.breaks || []).map(Number).filter(Number.isFinite);
  const labels = bin?.labels || [];
  const idx = labels.indexOf(label);
  if (idx >= 0 && breaks.length) return { lo: idx === 0 ? null : breaks[idx - 1], hi: idx < breaks.length ? breaks[idx] : null };
  let m = String(label).match(/^<\s*(-?[\d.]+)/);
  if (m) return { lo: null, hi: Number(m[1]) };
  m = String(label).match(/^[\u2265>=]+\s*(-?[\d.]+)/);
  if (m) return { lo: Number(m[1]), hi: null };
  m = String(label).match(/^(-?[\d.]+)\s*[\u2013-]\s*(-?[\d.]+)$/);
  if (m) return { lo: Number(m[1]), hi: Number(m[2]) };
  return { lo: null, hi: null };
}

// Side-by-side band comparison cards (High vs Low etc.) — clicked band highlighted.
function BandCompareCards({ cards, selected }) {
  const { measureCols, entries, totals } = cards;
  const fmt = (n) => (Number.isFinite(n) ? Number(n).toLocaleString(undefined, { maximumFractionDigits: 1 }) : "—");
  const selEntry = entries.find(([bv]) => bv === selected);
  const firstM = measureCols[0];
  return (
    <div className="space-y-2">
      <span className="text-xs text-[color:var(--ink-3)]">Every band side-by-side — the band you clicked is highlighted.</span>
      <div data-testid="band-compare-cards" className="grid gap-2" style={{ gridTemplateColumns: `repeat(${Math.min(entries.length, 4)}, minmax(0, 1fr))` }}>
        {entries.map(([bv, g]) => {
          const isSel = bv === selected;
          const selVal = selEntry?.[1]?.sums?.[firstM];
          const delta = !isSel && firstM && Number.isFinite(selVal) && selVal !== 0 && Number.isFinite(g.sums[firstM])
            ? ((g.sums[firstM] - selVal) / Math.abs(selVal)) * 100 : null;
          return (
            <div key={bv} data-testid={`band-compare-card-${bv}`}
              className={cn("rounded-xl border p-3 space-y-2",
                isSel ? "border-[color:rgba(255,107,53,0.5)] bg-[color:rgba(255,107,53,0.06)]" : "border-[color:var(--glass-border)] bg-[color:var(--surface-2)]")}>
              <div className="flex items-center justify-between gap-1">
                <span className={cn("text-sm font-semibold truncate", isSel ? "text-[#FF6B35]" : "text-[color:var(--ink-1)]")}>{bv}</span>
                {isSel && <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-[#FF6B35] text-white font-medium shrink-0">selected</span>}
              </div>
              <div className="text-[11px] text-[color:var(--ink-3)]">{g.n.toLocaleString()} {g.n === 1 ? "group" : "groups"}</div>
              {measureCols.map((c) => {
                const share = totals[c] ? (g.sums[c] || 0) / totals[c] * 100 : null;
                return (
                  <div key={c} className="space-y-0.5">
                    <div className="text-[10px] text-[color:var(--ink-3)] truncate">{c}</div>
                    <div className="text-sm font-semibold text-[color:var(--ink-1)]">{fmt(g.sums[c])}</div>
                    {share != null && (
                      <div className="flex items-center gap-1.5">
                        <div className="h-1 flex-1 rounded-full bg-[color:var(--glass-border)] overflow-hidden">
                          <div className="h-full rounded-full" style={{ width: `${Math.min(100, share)}%`, background: isSel ? "#FF6B35" : "var(--brand-ocean)" }} />
                        </div>
                        <span className="text-[10px] text-[color:var(--ink-3)] shrink-0">{share.toFixed(1)}%</span>
                      </div>
                    )}
                  </div>
                );
              })}
              {delta != null && (
                <div className={cn("text-[10px] font-medium", delta >= 0 ? "text-[#16A34A]" : "text-[#DC2626]")}>
                  {delta >= 0 ? "+" : "−"}{Math.abs(delta).toFixed(1)}% vs {selected}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// Detail view behind a visual: "Summarized" = the grouped/aggregated values that
// produced the chart; "Underlying rows" = row-level records limited to the fields
// the visual actually uses (with an optional all-columns toggle).
export function UnderlyingDataDialog({ open, onOpenChange, ctx }) {
  const [tab, setTab] = useState("summary");
  const [allCols, setAllCols] = useState(false);
  const [summary, setSummary] = useState(null);
  const [rowsData, setRowsData] = useState(null);
  const [compareData, setCompareData] = useState(null);
  const [model, setModel] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const widget = ctx?.widget;
  const publicToken = ctx?.publicToken || null;
  const filters = useMemo(() => ctx?.filters || [], [ctx]);
  // Item 26: shared / published viewers query through the token-gated public endpoints
  const runSem = useCallback((sq) => (publicToken
    ? api.post("/public/model/query", { token: publicToken, dataset_id: widget?.dataset_id, semantic_query: sq }, { timeout: 45000 })
    : api.post(`/datasets/${widget?.dataset_id}/model/query`, sq)), [publicToken, widget?.dataset_id]);
  const runRows = useCallback((body) => (publicToken
    ? api.post("/public/rows", { token: publicToken, dataset_id: widget?.dataset_id, ...body })
    : api.post(`/datasets/${widget?.dataset_id}/rows`, body)), [publicToken, widget?.dataset_id]);
  const filtersKey = JSON.stringify(filters);
  const band = ctx?.band || null;
  const bandKey = JSON.stringify(band || null);
  const config = useMemo(() => widget?.config || {}, [widget]);
  const hasGoverned = [...(config.dimensions || []), ...(config.measures || []), ...(config.tooltip_measures || [])].some((r) => r.key && !r.field);
  const hasAgg = (config.dimensions || []).length > 0 || (config.measures || []).length > 0;

  useEffect(() => { if (open) { setTab(hasAgg ? "summary" : "rows"); setAllCols(false); setSummary(null); setRowsData(null); setCompareData(null); setError(null); } }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  // Semantic model (only needed to resolve governed keys -> physical fields)
  useEffect(() => {
    if (!open || !widget?.dataset_id || !hasGoverned || publicToken) return;
    api.get(`/datasets/${widget.dataset_id}/model`).then(({ data }) => setModel(data)).catch(() => {});
  }, [open, widget?.dataset_id, hasGoverned, publicToken]);

  const visualCols = useMemo(() => usedColumns(config, model), [config, model]);

  // Summarized: run the exact aggregated query behind the visual
  useEffect(() => {
    if (!open || !widget?.dataset_id || tab !== "summary" || !hasAgg) return;
    setLoading(true); setError(null);
    const sq = buildSemanticQuery(config, filters);
    runSem(sq)
      .then(({ data }) => {
        let rows2 = data.rows || [];
        if (band?.dim) rows2 = rows2.filter((r) => String(r[bandAlias(band.dim)]) === String(band.value));
        setSummary({ columns: data.columns || [], rows: rows2 });
      })
      .catch((e) => setError(e?.response?.data?.detail || "Failed to load summarized data"))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, tab, widget?.dataset_id, filtersKey, bandKey]);

  // Underlying rows: only the fields used by this visual (unless allCols)
  useEffect(() => {
    if (!open || !widget?.dataset_id || tab !== "rows") return;
    if (hasGoverned && !model && !allCols) return; // wait for key resolution
    let active = true;
    (async () => {
      setLoading(true); setError(null);
      try {
        const extra = [];
        if (band?.dim?.bin) {
          const { lo, hi } = rangeFromLabel(band.value, band.dim.bin);
          if (lo != null) extra.push({ field: band.dim.field, op: "gte", value: lo });
          if (hi != null) extra.push({ field: band.dim.field, op: "lt", value: hi });
        } else if (band?.dim?.band) {
          // Post-aggregation band: restrict rows to the other-dimension groups inside this band
          const alias = bandAlias(band.dim);
          const { data: sm } = await runSem(buildSemanticQuery(config, filters));
          const o = (config.dimensions || []).find((x) => x && x !== band.dim && (x.field || x.key));
          if (o) {
            const out = o.key || o.alias || (o.date_part && o.date_part !== "none" ? `${o.field}_${o.date_part}` : o.field);
            const keyMap = {};
            (model?.dimensions || []).forEach((d2) => { if (d2.key && d2.field) keyMap[d2.key] = d2.field; });
            const field = o.field || keyMap[o.key];
            const vals = [...new Set((sm.rows || []).filter((r) => String(r[alias]) === String(band.value)).map((r) => r[out]))];
            if (field && vals.length) {
              const f = { field, op: "in", value: vals };
              if (o.date_part && o.date_part !== "none") f.date_part = o.date_part;
              extra.push(f);
            }
          }
        }
        const body = { filters: [...cleanFilters(filters), ...extra], limit: 500 };
        if (!allCols && visualCols.length) body.columns = visualCols;
        const { data } = await runRows(body);
        if (active) setRowsData(data);
      } catch (e) {
        if (active) setError(e?.response?.data?.detail || "Failed to load underlying data");
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, tab, allCols, widget?.dataset_id, filtersKey, model, bandKey]);

  // Band comparison: side-by-side stats for every band (High vs Low etc.)
  useEffect(() => {
    if (!open || !widget?.dataset_id || tab !== "compare" || !band?.dim) return;
    setLoading(true); setError(null);
    runSem(buildSemanticQuery(config, filters))
      .then(({ data }) => setCompareData({ columns: data.columns || [], rows: data.rows || [] }))
      .catch((e) => setError(e?.response?.data?.detail || "Failed to load band comparison"))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, tab, widget?.dataset_id, filtersKey, bandKey]);

  const compareCards = useMemo(() => {
    if (!compareData || !band?.dim) return null;
    const alias = bandAlias(band.dim);
    const dimOuts = new Set((config.dimensions || []).map((d) =>
      bandAlias(d) || d.key || (d.date_part && d.date_part !== "none" ? `${d.field}_${d.date_part}` : d.field)));
    const measureCols = compareData.columns.filter((c) => !dimOuts.has(c));
    const byBand = new Map();
    compareData.rows.forEach((r) => {
      const bv = String(r[alias]);
      if (!byBand.has(bv)) byBand.set(bv, { n: 0, sums: {} });
      const g = byBand.get(bv);
      g.n += 1;
      measureCols.forEach((c) => { const v = Number(r[c]); if (Number.isFinite(v)) g.sums[c] = (g.sums[c] || 0) + v; });
    });
    const totals = {};
    measureCols.forEach((c) => { totals[c] = [...byBand.values()].reduce((s, g) => s + (g.sums[c] || 0), 0); });
    return { alias, measureCols, entries: [...byBand.entries()], totals };
  }, [compareData, bandKey, config]); // eslint-disable-line react-hooks/exhaustive-deps

  const active = tab === "summary" ? summary : tab === "rows" ? rowsData : null;

  const exportCSV = () => {
    if (!active) return;
    const blob = new Blob([toCSV(active.columns, active.rows)], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `${widget?.title || "underlying-data"}-${tab}.csv`;
    a.click(); URL.revokeObjectURL(url);
    toast.success("CSV downloaded");
  };

  const appliedChips = cleanFilters(filters).map((f, i) => (
    <span key={i} data-testid="underlying-filter-chip" className="text-[10px] px-2 py-0.5 rounded-full bg-[color:var(--surface-2)] text-[color:var(--ink-2)] border border-[color:var(--glass-border)]">
      {f.field} {f.op || "eq"} {Array.isArray(f.value) ? f.value.join("–") : String(f.value)}
    </span>
  ));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl" data-testid="underlying-data-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2"><Table2 className="w-5 h-5 text-[color:var(--brand-ocean)]" />{widget?.title || "Underlying data"}</DialogTitle>
          <DialogDescription>Data behind this visual, scoped to the fields it uses, with all its filters applied.</DialogDescription>
        </DialogHeader>
        {(appliedChips.length > 0 || band) && (
          <div className="flex flex-wrap gap-1.5">
            {band && (
              <span data-testid="underlying-band-chip" className="text-[10px] px-2 py-0.5 rounded-full bg-[color:rgba(255,107,53,0.12)] text-[#FF6B35] border border-[color:rgba(255,107,53,0.35)] font-medium">
                {band.dim?.label || bandAlias(band.dim)}: {band.value}
              </span>
            )}
            {appliedChips}
          </div>
        )}
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div className="flex items-center gap-1">
            {hasAgg && (
              <button data-testid="underlying-tab-summary" onClick={() => setTab("summary")}
                className={cn("flex items-center gap-1 text-[11px] px-2.5 py-1 rounded-lg transition-colors",
                  tab === "summary" ? "bg-[color:var(--brand-ocean)] text-white" : "text-[color:var(--ink-3)] hover:bg-[color:var(--surface-2)]")}>
                <Sigma className="w-3 h-3" />Summarized
              </button>
            )}
            <button data-testid="underlying-tab-rows" onClick={() => setTab("rows")}
              className={cn("flex items-center gap-1 text-[11px] px-2.5 py-1 rounded-lg transition-colors",
                tab === "rows" ? "bg-[color:var(--brand-ocean)] text-white" : "text-[color:var(--ink-3)] hover:bg-[color:var(--surface-2)]")}>
              <Rows3 className="w-3 h-3" />Underlying rows
            </button>
            {band?.dim && hasAgg && (
              <button data-testid="underlying-tab-compare" onClick={() => setTab("compare")}
                className={cn("flex items-center gap-1 text-[11px] px-2.5 py-1 rounded-lg transition-colors",
                  tab === "compare" ? "bg-[color:var(--brand-ocean)] text-white" : "text-[color:var(--ink-3)] hover:bg-[color:var(--surface-2)]")}>
                <Columns2 className="w-3 h-3" />Compare bands
              </button>
            )}
          </div>
          <div className="flex items-center gap-3">
            {tab === "rows" && visualCols.length > 0 && (
              <label className="flex items-center gap-1.5 text-[11px] text-[color:var(--ink-3)]">
                <Switch data-testid="underlying-all-columns" checked={allCols} onCheckedChange={setAllCols} />All columns
              </label>
            )}
            <Button data-testid="underlying-export-csv" size="sm" variant="outline" onClick={exportCSV} disabled={!active} className="bg-[color:var(--surface)]"><Download className="w-3.5 h-3.5 mr-1" />Export CSV</Button>
          </div>
        </div>
        {loading ? (
          <div className="py-14 flex items-center justify-center"><Loader2 className="w-5 h-5 animate-spin text-[color:var(--brand-ocean)]" /></div>
        ) : error ? (
          <div className="py-10 text-center text-sm text-[color:var(--danger)]"><AlertTriangle className="w-5 h-5 mx-auto mb-1" />{error}</div>
        ) : tab === "summary" && summary ? (
          <>
            <span data-testid="underlying-rowcount" className="text-xs text-[color:var(--ink-3)]">{summary.rows.length.toLocaleString()} summarized {summary.rows.length === 1 ? "group" : "groups"} — exactly what this visual plots</span>
            <ResultTable columns={summary.columns} rows={summary.rows} testid="underlying-summary-table" />
          </>
        ) : tab === "rows" && rowsData ? (
          <>
            <span data-testid="underlying-rowcount" className="text-xs text-[color:var(--ink-3)]">
              Showing {rowsData.rows.length.toLocaleString()} of {rowsData.total_rows.toLocaleString()} matching rows{!allCols && visualCols.length ? ` · ${rowsData.columns.length} of the visual's fields` : ""}
            </span>
            <ResultTable columns={rowsData.columns} rows={rowsData.rows} testid="underlying-rows-table" />
          </>
        ) : tab === "compare" && compareCards ? (
          <BandCompareCards cards={compareCards} selected={String(band?.value)} />
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
